import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useStoreState } from 'easy-peasy';
import { withFormik, Formik, Form, FastField } from 'formik';
import { Tabs, Tab } from '@material-ui/core';
import _e from 'common/localeUtil';
import ChooseImage from '../ChooseImage';
import FieldWrapper from '../form/FieldWrapper';
import GenericDialog from './GenericDialog';
import { a11yProps, TabPanel } from '../tab/tab';
import ColorPicker from '../color-picker/ColorPicker';

import {
	findGrapesJSComponent,
	getImageUrlFromBackground,
	getResizeImageUrlWidth,
	getStyleGrapesJSComponent,
	hideSrcFromImageGrapesJSComponent,
	setStyleGrapesJSComponent,
} from '../../utils';
import { readRemoteImageAsBase64, dataURLToFile } from '../../utils/imageUtils';

const useStyles = makeStyles((theme) => ({
	tabPanelWrapper: {
		padding: theme.spacing(1),
		// backgroundColor: '#f2f2f2'
	},
	contentWrapper: {
		justifyContent: 'center',
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		backgroundColor: theme.palette.background.paper,
	},
	line: {
		border: '1px solid #bababa',
		width: '100%',
	},
	dialogContent: {
		// padding: '14px 28px'
		padding: 0,
	},
	inputColor: {
		// marginTop: theme.spacing(1.5),
		marginLeft: theme.spacing(2),
	},
	colorPickerWrapperStyle: {
		textAlign: 'center',
		display: 'flex',
		justifyContent: 'center',
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
	},
	colorPickerWrapperActionStyle: {
		textAlign: 'right',
	},
	browseImage: {
		marginTop: 14,
		marginBottom: 42,
		display: 'block',
		fontSize: '1rem',
	},
	uploadImageError: {
		color: 'red',
		margin: '1rem 0',
	},
	expectedImageSize: {
		color: '#215BAB',
		margin: '1rem 0',
	},
}));

const ChangeBackgroundDialog = ({
	open,
	sectionId,
	handleClose,
	onImageChanged,
	title,
	options: {
		colorChangeable,
		maxWidth,
		maxHeight,
		showImageEditor,
		cropRatio,
		unsplashChangeable = true,
		expectedImageSizeMessage,
		acceptFiles,
	},
}) => {
	console.log('ChangeBackgroundDialog', { maxWidth, maxHeight, cropRatio });

	const classes = useStyles();
	const uploaderInputRef = React.useRef();
	const inputRef = React.useRef();
	// const templateId = useStoreState(state => state.template.template);
	let defaultColor = getStyleGrapesJSComponent(sectionId, 'background') || '#000';
	let defaultAvatar = getStyleGrapesJSComponent(sectionId, 'background') || '#000';
	// Process default color and avatar
	if (!defaultColor.startsWith('#')) {
		defaultColor = '#000';
	}
	defaultAvatar = getImageUrlFromBackground(defaultAvatar);

	const [selectedTab, setSelectedTab] = useState(0);
	const [selectedColor, setSelectedColor] = useState(defaultColor);
	const [error, setError] = useState('');
	const adviserData = useStoreState((state) => state.template.adviserData);
	const { id } = adviserData;

	const _onImageChanged = (imageUrl) => {
		const updatedImageUrl = getResizeImageUrlWidth(imageUrl, 2000);
		const comp = findGrapesJSComponent(sectionId);

		window.gm_track(`Set Background: Image changed`, { sectionId, imageUrl });
		console.log(`Set Background: Image changed`, {
			comp,
			isImage: comp?.is('image') || comp?.get('tagName') === 'img',
			sectionId,
			imageUrl,
		});

		if (comp?.is('image') || comp?.get('tagName') === 'img') {
			console.log('onImageChanged', comp, updatedImageUrl);
			comp.set('src', updatedImageUrl);

			// For template 7, need to update mobile image too
			if (window.currentTemplate === 'template7') {
				window.grapesjs.Canvas.getWindow().applyDesktopImageToMobileImageTemplate7();
			}
		} else {
			setStyleGrapesJSComponent(
				sectionId,
				{
					background: 'lightgray',
					'background-color': 'transparent',
					'background-size': 'cover',
					'-webkit-background-size': 'cover',
					'-moz-background-size': 'cover',
					'-o-background-size': 'cover',
					'background-repeat': 'no-repeat',
					'background-position': 'center center',
					'background-image': `url(${updatedImageUrl});`,
				},
				['background'],
			);
			hideSrcFromImageGrapesJSComponent(sectionId);
		}
	};

	const onColorChanged = (color) => {
		window.gm_track(`Set Background: Set color`, { color, sectionId });

		console.log(sectionId);
		setStyleGrapesJSComponent(sectionId, {
			'background-image': 'none',
			'background-color': color,
		});

		hideSrcFromImageGrapesJSComponent(sectionId);
	};

	const handleColorChange =
		(close) =>
		({ hex: color }) => {
			console.log('handleColorChange', color, sectionId);
			if (!close) {
				setSelectedColor(color);
				onColorChanged(color);
				window.GM_SelectedColor = color;
			}

			close && handleClose();
		};

	const onImageUploaded = (imageData) => {
		console.log('onImageUploaded', imageData);
		const { url } = imageData;
		_onImageChanged(url);
		onImageChanged && onImageChanged(url);
		handleClose();
	};

	const handleImageSelected = (imageUrl) => {
		if (showImageEditor) {
			setSelectedTab(0);

			readRemoteImageAsBase64(imageUrl).then((base64Img) => {
				const uploader = uploaderInputRef.current.uploader.current;
				uploader.setFiles([dataURLToFile(base64Img)]);
			});
		} else {
			_onImageChanged(imageUrl);
			onImageChanged && onImageChanged(imageUrl);
			handleClose();
		}
	};

	const handleTabChange = (event, newValue) => {
		setSelectedTab(newValue);
	};

	const onErrorCallBack = (message) => {
		setError(message);
	};

	const renderContent = () => {
		console.log('renderContent', showImageEditor, selectedColor, sectionId, colorChangeable);
		const comp = findGrapesJSComponent(sectionId);
		const showColorPanel = !comp?.is('image') && colorChangeable;

		const expectedSizeMsg =
			expectedImageSizeMessage ||
			`${_e('expected_image_size')}: ${maxWidth}x${maxHeight} pixels`;

		return (
			<>
				<Tabs
					variant="fullWidth"
					value={selectedTab}
					onChange={handleTabChange}
					aria-label="tabs"
					className={classes.tabs}
				>
					<Tab label={_e('upload_image')} {...a11yProps(0)} className={classes.tab} />
					{unsplashChangeable && (
						<Tab
							label={_e('browse_unsplash')}
							{...a11yProps(1)}
							className={classes.tab}
						/>
					)}
					{showColorPanel && (
						<Tab label={_e('set_color')} {...a11yProps(2)} className={classes.tab} />
					)}
				</Tabs>
				<div className={classes.tabPanelWrapper}>
					<TabPanel value={selectedTab} index={0} className={classes.contentWrapper}>
						<div>
							<Formik initialValues={{}}>
								{() => (
									<Form>
										<FastField name="background_picture" type="image">
											{(fieldProps) => (
												<FieldWrapper
													{...fieldProps}
													maxWidth={maxWidth}
													iconWidth={maxHeight}
													iconHeight={160}
													s3Path={`client/${id}/agent_website/`}
													isPublic
													notAvatar
													width={400}
													height={250}
													onChangeCallBack={onImageUploaded}
													onErrorCallBack={onErrorCallBack}
													defaultAvatar={defaultAvatar}
													ref={uploaderInputRef}
													showImageEditor={showImageEditor}
													inputRef={inputRef}
													cropRatio={cropRatio}
													acceptFiles={acceptFiles}
												/>
											)}
										</FastField>
									</Form>
								)}
							</Formik>
							{error && <div className={classes.uploadImageError}>{error}</div>}
							<div className={classes.expectedImageSize}>{expectedSizeMsg}</div>
						</div>
					</TabPanel>
					<TabPanel value={selectedTab} index={1}>
						<ChooseImage maxWidth="md" onImageClick={handleImageSelected} />
					</TabPanel>
					<TabPanel value={selectedTab} index={2}>
						<div className={classes.colorPickerWrapperStyle}>
							<ColorPicker
								onChange={handleColorChange(false)}
								defaultHex={selectedColor}
							/>
						</div>
					</TabPanel>
				</div>
			</>
		);
	};

	return (
		<GenericDialog
			open={open}
			handleClose={handleClose}
			showTitleDivider
			fullWidth
			disableContentPadding
			title={title}
			content={renderContent()}
			confirmAction={handleClose}
			maxWidth="md"
		/>
	);
};

ChangeBackgroundDialog.propTypes = {
	sectionId: PropTypes.string,
	open: PropTypes.bool.isRequired,
	handleClose: PropTypes.func.isRequired,
	options: PropTypes.shape({
		colorChangeable: PropTypes.bool,
		unsplashChangeable: PropTypes.bool,
		maxWidth: PropTypes.number,
		maxHeight: PropTypes.number,
		showImageEditor: PropTypes.bool,
		cropRatio: PropTypes.number,
		expectedImageSizeMessage: PropTypes.string,
		acceptFiles: PropTypes.string,
	}),
	onImageChanged: PropTypes.func,
	title: PropTypes.string,
};

ChangeBackgroundDialog.defaultProps = {
	options: {
		colorChangeable: true,
		unsplashChangeable: true,
	},
	title: _e('set_background'),
};

export default withFormik({
	mapPropsToValues: () => ({ inputSearch: 'background' }),
	// Custom sync validation
	validate: (values) => {
		console.log(values);
	},
	displayName: 'ChangeBackgroundDialog',
})(ChangeBackgroundDialog);
